import { useEffect } from 'react';
import { Home, Users, Shield, Heart, Eye, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { referralService, ReferralPlan } from '@/services/referralService';
import { isTierAllowed } from '@/lib/cookie-consent-core';

const ReferralWelcome = () => {
  const { openModal } = useEarlyAdopter();

  // Capture referral code on mount (gated behind Tier 3 cookie consent)
  useEffect(() => {
    if (!isTierAllowed(3)) return;
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      referralService.persistReferralCode(refCode);
    }
    referralService.persistUTMParams();
  }, []);

  const handleContinue = (plan: ReferralPlan, role: string) => {
    // Persist the plan selection
    referralService.persistPlanSelection(plan);
    
    // Store role and plan in sessionStorage for the modal to pick up
    sessionStorage.setItem('preselected_role', role);
    sessionStorage.setItem('preselected_plan', plan);
    
    // Open the early adopter modal with source info
    const source = plan === 'thrivemax' 
      ? 'Referral Welcome - Older Adult' 
      : 'Referral Welcome - Family Member';
    openModal(source);
  };

  return (
    <>
      <SEO
        title="Welcome to SeniorThrive"
        description="You were invited to SeniorThrive. Choose your path to get started with safe, independent living at home."
        noindex
      />
      
      <div className="min-h-screen bg-gradient-card">
        {/* Hero Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Welcome. Let's Get You to the Right Place.
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
                You were invited here because someone you trust thought SeniorThrive could be helpful.
              </p>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                SeniorThrive supports people in different ways depending on who you're here for.
                <br />
                <span className="font-medium text-foreground">Please choose the option that fits you best.</span> You can change this later.
              </p>
            </div>

            {/* Two Path Cards */}
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {/* Older Adult Card */}
              <Card className="border-2 border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
                <CardContent className="p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-full">
                      <Home className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="text-xl lg:text-2xl font-bold text-foreground">
                      I'm an Older Adult
                    </h2>
                  </div>
                  
                  <p className="text-muted-foreground mb-6">
                    I want to live safely and confidently in my own home.
                  </p>

                  <div className="mb-6">
                    <p className="text-sm font-bold text-foreground mb-3">This is a good fit if you:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        Live at home and want to stay independent
                      </li>
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        Want help spotting safety risks before they become problems
                      </li>
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        Want support without giving up control
                      </li>
                    </ul>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm font-bold text-foreground mb-3">What this gives you:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        A simple, private dashboard
                      </li>
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Eye className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        Help understanding safety and wellness at home
                      </li>
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Users className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        The option to invite family when and if you choose
                      </li>
                    </ul>
                  </div>

                  <Button 
                    onClick={() => handleContinue('thrivemax', 'older_adult')}
                    className="w-full bg-gradient-cta hover:opacity-90"
                    size="lg"
                  >
                    Continue as an Older Adult
                  </Button>
                </CardContent>
              </Card>

              {/* Family Member Card */}
              <Card className="border-2 border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
                <CardContent className="p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-full">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="text-xl lg:text-2xl font-bold text-foreground">
                      I'm a Family Member or Loved One
                    </h2>
                  </div>
                  
                  <p className="text-muted-foreground mb-6">
                    I support an older adult and want us all on the same page.
                  </p>

                  <div className="mb-6">
                    <p className="text-sm font-bold text-foreground mb-3">This is a good fit if you:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        Help a parent, partner, or loved one
                      </li>
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        Want clarity without hovering or constant check-ins
                      </li>
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        Want better communication and fewer surprises
                      </li>
                    </ul>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm font-bold text-foreground mb-3">What this gives you:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Eye className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        A shared view of safety and wellness
                      </li>
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Heart className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        Better coordination with family and caregivers
                      </li>
                      <li className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        More peace of mind through clarity
                      </li>
                    </ul>
                  </div>

                  <Button 
                    onClick={() => handleContinue('thrivefamily', 'family_member')}
                    className="w-full bg-gradient-cta hover:opacity-90"
                    size="lg"
                  >
                    Continue as a Family Member
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Footer Note */}
            <Card className="mt-12 bg-muted/30 border-border/50">
              <CardContent className="p-6 text-center">
                <h3 className="font-bold text-foreground mb-2">One Helpful Thing to Know</h3>
                <p className="text-muted-foreground text-sm max-w-2xl mx-auto">
                  SeniorThrive is not about monitoring or taking control.
                  <br />
                  It's about helping people stay safe, connected, and confident at home.
                </p>
                <p className="text-sm text-muted-foreground/80 mt-3">
                  This choice helps us set up the right experience for you from the start.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </>
  );
};

export default ReferralWelcome;
